import React from "react";
import App from "./App";
import { createStore, applyMiddleware } from "redux";
import { persistStore, persistReducer } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import storage from "redux-persist/lib/storage";
import { composeWithDevTools } from '@redux-devtools/extension';
import createSagaMiddleware from "redux-saga";
import { Provider } from "react-redux";
import { unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import Routes from "./routes";
import { ThemeProvider, StyledEngineProvider } from "@mui/material/styles";
import theme from "./theme";
import rootSaga from "./sagas";
import { historyContext } from "./reducers";  // Import it from reducers
import rootReducer from "./reducers";
import { setupAxiosInterceptors } from "./services/axiosInterceptors";
import "./index.css";


const { createReduxHistory, routerMiddleware } = historyContext;
const sagaMiddleware = createSagaMiddleware();

const middlewares = [sagaMiddleware, routerMiddleware];
const persistConfig = {
    key: "root",
    storage,
    blacklist: ['router']
};
let historyInstance: any = null;

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const getHistory = () => historyInstance;

interface SetupState {
    rehydrated: boolean;
}

export default class Setup extends React.PureComponent<{}, SetupState> {
    storeWithMiddleware: any;
    history: any;
    unsubscribe: any;

    constructor(props: {}) {
        super(props);

        this.state = {
            rehydrated: false,
        };
        this.createStore();
    }

    createStore = () => {
        const arr = applyMiddleware(...middlewares);
        this.storeWithMiddleware =
            composeWithDevTools(arr)(createStore)(persistedReducer);
        this.history = createReduxHistory(this.storeWithMiddleware);
        historyInstance = this.history;
        sagaMiddleware.run(rootSaga);
        
        // Setup axios interceptors after store creation
        setupAxiosInterceptors(this.storeWithMiddleware);

        // Subscribe to store changes to check business alias
        this.unsubscribe = this.storeWithMiddleware.subscribe(() => {
            const state = this.storeWithMiddleware.getState();
            const alias = window.location.pathname.split("/")[1];
            const userConfig = state.auth?.userConfig;
            const isRehydrated = state._persist?.rehydrated;

            // Only check after rehydration is complete
            if (isRehydrated && userConfig?.businessAlias && alias && userConfig.businessAlias !== alias) {
                console.log("BUSINESS ALIAS CHANGED SIGNOUT");
                this.storeWithMiddleware.dispatch({ type: "LOGOUT" });
                this.history.push(`/${alias}/signin`);
            }
        });
    };

    componentWillUnmount() {
        if (this.unsubscribe) {
            this.unsubscribe();
        }
    }

    render() {
        const persistor = persistStore(this.storeWithMiddleware, null, () => {
            // Set timeout after rehydration completes
            setTimeout(() => {
                this.setState({ rehydrated: true });
            }, 100);
        });
        
        return (
            <React.Fragment>
                <Provider store={this.storeWithMiddleware}>
                    <HistoryRouter history={this.history}>
                        <StyledEngineProvider injectFirst>
                            <ThemeProvider theme={theme}>
                                <PersistGate loading={null} persistor={persistor}>
                                    {this.state.rehydrated ? (
                                        <>
                                            <App />
                                            <Routes />
                                        </>
                                    ) : null}
                                </PersistGate>
                            </ThemeProvider>
                        </StyledEngineProvider>
                    </HistoryRouter>
                </Provider>
            </React.Fragment>
        );
    }
}
